import { Badge } from "flowbite-react";
import { ImagePlaceholder } from "../icons";
import { StockBadge, stockColor } from "../../helpers/merch-manager/helper";
import { useRef, useState } from "react";
import { CustomModal } from "../customModal";
import RequestMerchItem from "../../components/merchandise/catalogue/requestMerchItem";
import Lightbox from "../lightbox";

export const LoadingSkeleton = () => {
  return (
    <div className="space-y-2">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="h-16 rounded-xl bg-gray-100 dark:bg-gray-800 animate-pulse"
        />
      ))}
    </div>
  );
};

export const AssetGrid = ({ assets, imgIndex, setImgIndex, size }) => {
  return (
    assets.length > 1 &&
    assets.map((a, i) => (
      <button
        key={a.id}
        onClick={() => setImgIndex(i)}
        className={`${size || "w-10 h-10"} rounded-lg overflow-hidden border-2 transition-colors shrink-0 ${
          i === imgIndex
            ? "border-primary"
            : "border-transparent opacity-60 hover:opacity-100"
        }`}
      >
        <img
          src={a.url}
          alt={a.fileName}
          className="w-full h-full object-cover"
        />
      </button>
    ))
  );
};

export const MerchCard = ({ item }) => {
  const [imgIndex, setImgIndex] = useState(0);
  const [isEnlarged, setIsEnlarged] = useState(false);
  const badge = StockBadge(item.item_count);
  const outOfStock = Number(item.item_count) === 0;
  const modalRef = useRef(null);
  const openRef = useRef(null);

  return (
    <div className="flex flex-col rounded-2xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden hover:border-gray-300 dark:hover:border-gray-600 transition-all">
      {/* Image */}
      {item.assets?.length > 0 ? (
        <div
          className="relative w-full bg-gray-50 dark:bg-gray-900"
          style={{ aspectRatio: "16/9" }}
        >
          {item.assets.map((a, i) => (
            <img
              key={a.id}
              src={a.url}
              alt={a.fileName}
              className="absolute p-1 inset-0 w-full h-full object-contain transition-opacity duration-150"
              style={{ opacity: i === imgIndex ? 1 : 0 }}
              onClick={() => setIsEnlarged(true)}
            />
          ))}
        </div>
      ) : (
        <div
          className="w-full bg-gray-100 dark:bg-gray-900 flex items-center justify-center"
          style={{ aspectRatio: "16/9" }}
        >
          <ImagePlaceholder />
        </div>
      )}
      <Lightbox
        isOpen={isEnlarged}
        onClose={() => setIsEnlarged(false)}
        imgItem={item}
        imgIndex={imgIndex}
        imgGrid={
          <AssetGrid
            assets={item.assets}
            imgIndex={imgIndex}
            setImgIndex={setImgIndex}
            size={"w-[5rem] h-[5rem]"}
          />
        }
      />
      <div className="flex flex-row gap-1.5 shrink-0 p-3">
        <AssetGrid
          assets={item.assets}
          imgIndex={imgIndex}
          setImgIndex={setImgIndex}
        />
      </div>

      {/* Info */}
      <div className="flex flex-col gap-3 p-4 flex-1">
        <div className="flex flex-col-reverse items-start justify-between gap-2 mt-auto">
          <div className="w-full min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
              {item.item_name}
            </p>
            {item.item_purpose && (
              <p className="text-xs text-gray-400 mt-0.5 truncate">
                {item.item_purpose}
              </p>
            )}
          </div>

          <div className="flex flex-row gap-2 items-center">
            {item.restock_expected && Number(item.item_count) === 0 ? (
              <div className="inline-flex">
                <Badge color="indigo" size="xs">
                  Ordered · Available soon
                </Badge>
              </div>
            ) : (
              <Badge color={badge.color} size="xs" className="shrink-0">
                {badge.label}
              </Badge>
            )}
            {item.storage_location && (
              <Badge color="purple" size="xs" className="shrink-0">
                {{
                  syd: "Sydney",
                  melb: "Melbourne",
                }[item.storage_location] || item.storage_location}
              </Badge>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          <span className={`text-xl font-bold ${stockColor(item.item_count)}`}>
            {item.item_count}
          </span>
          <span className="text-xs text-gray-400">in stock</span>
        </div>
        <CustomModal
          modalRef={modalRef}
          openRef={openRef}
          closeOnOutsideClick={true}
          customBtn={
            <button
              onClick={() => openRef.current()}
              disabled={outOfStock}
              className="w-full py-2 rounded-xl bg-primary text-white text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {outOfStock ? "Out of stock" : "Request item"}
            </button>
          }
        >
          <div className="w-full sm:w-[480px]">
            <RequestMerchItem item={item} modalRef={modalRef} />
          </div>
        </CustomModal>
      </div>
    </div>
  );
};

export const EmptyState = ({ icon, text }) => (
  <div className="flex flex-col items-center justify-center py-16 text-gray-300 dark:text-gray-600">
    {icon}
    <p className="text-sm mt-3">{text}</p>
  </div>
);

export const TabSwitcher = ({
  onClick,
  tab,
  activeTab,
  count,
  title,
  value,
  children,
}) => {
  return (
    <button
      onClick={onClick}
      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
        activeTab === value
          ? "bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow-sm"
          : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
      }`}
    >
      <span>{title}</span>

      {children ? (
        children
      ) : count > 0 ? (
        <span className="hidden sm:inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-white text-xs font-bold">
          {count}
        </span>
      ) : null}
    </button>
  );
};
